/**
 * Shared utilities for {@link RoleRow}, {@link RoleCard} and {@link RolesDataTable}.
 */
import type { Role } from '@/types/role.types';
import type { RoleFilters } from './role-configs';

/**
 * Resolves a color token string to a valid CSS color value.
 * Passes through hex / rgb / hsl values unchanged;
 * wraps bare CSS custom property names in var(--…).
 */
export function resolveColorToken(token: string | null | undefined): string | undefined {
  if (!token) return undefined;
  if (token.startsWith('#') || token.startsWith('rgb') || token.startsWith('hsl')) return token;
  return `var(--${token})`;
}

/** Badge color: `active` is success; everything else is error. */
export function getRoleStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}

/** System / default / custom label for a role row. */
export function getRoleTypeLabel(role: { isSystem?: boolean; isDefault?: boolean }): string {
  if (role.isSystem) return 'Rôle système';
  if (role.isDefault) return 'Rôle par défaut';
  return 'Rôle personnalisé';
}

/** Unique role groups (code / label / icon / color) from a roles list. */
export function getDistinctRoleGroups(roles: Role[]): Array<{
  code: string;
  label: string;
  icon: string | null;
  color: string | null;
}> {
  const seen = new Map<
    string,
    { code: string; label: string; icon: string | null; color: string | null }
  >();
  roles.forEach((r) => {
    if (r.roleGroup && !seen.has(r.roleGroup)) {
      seen.set(r.roleGroup, {
        code: r.roleGroup,
        label: r.roleGroupLabel ?? r.roleGroup,
        icon: r.roleGroupIcon ?? null,
        color: r.roleGroupColor ?? null,
      });
    }
  });
  return Array.from(seen.values());
}

/** Map UI filters + pagination to role list params. */
export function toRoleListParams(
  filters: RoleFilters,
  page: number,
  pageSize: number,
): {
  page: number;
  pageSize: number;
  roleGroups?: string[];
  statuses?: RoleFilters['statuses'];
  modes?: RoleFilters['modes'];
  search?: string;
  minHierarchyLevel?: number;
  maxHierarchyLevel?: number;
} {
  return {
    page,
    pageSize,
    roleGroups: filters.roleGroups.length > 0 ? filters.roleGroups : undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    modes: filters.modes.length > 0 ? filters.modes : undefined,
    search: filters.searchTerm,
    minHierarchyLevel: filters.minHierarchyLevel,
    maxHierarchyLevel: filters.maxHierarchyLevel,
  };
}

/** True when any group / status / mode / search / hierarchy filter is set. */
export function hasRoleActiveFilters(filters: RoleFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.modes.length > 0 ||
    filters.roleGroups.length > 0 ||
    !!filters.searchTerm ||
    filters.minHierarchyLevel !== undefined ||
    filters.maxHierarchyLevel !== undefined
  );
}
